/**
 * Conversions Page
 * Displays pending and converted submissions for lead owners
 * Module 7: Lead Conversion & Referral Fee
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import { Divider } from 'primereact/divider';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Tooltip } from 'primereact/tooltip';
import { SelectButton } from 'primereact/selectbutton';
import { Paginator } from 'primereact/paginator';
import { Skeleton } from 'primereact/skeleton';

import {
    conversionService,
    REJECTION_REASONS,
    getStatusConfig,
    formatCurrency,
    formatDate,
    formatTimeAgo,
    formatPercentage
} from '../../services/conversionService';

import { getLoanProductConfig, PRODUCT_TYPE_DETAILED_OPTIONS } from '../../services/marketplaceService';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// Lead Deals section tabs
const LEAD_DEALS_TABS = [
    { label: 'My Deals', icon: 'pi pi-briefcase', route: '/app/collaborations' },
    { label: 'Lead Conversions', icon: 'pi pi-check-circle', route: '/app/collaborations/conversions' }
];

// Outline style for Tags - border only, no solid fill
const outlineTagStyle = (severity) => {
    const colors = {
        warning: { background: 'transparent', border: '1.5px solid #EAB308', color: '#EAB308' },
        info: { background: 'transparent', border: '1.5px solid #3B82F6', color: '#3B82F6' },
        success: { background: 'transparent', border: '1.5px solid #22C55E', color: '#22C55E' },
        danger: { background: 'transparent', border: '1.5px solid #EF4444', color: '#EF4444' },
        secondary: { background: 'transparent', border: '1.5px solid #94A3B8', color: '#64748B' },
    };
    return colors[severity] || colors.secondary;
};

const LOAN_TYPE_OPTIONS = PRODUCT_TYPE_DETAILED_OPTIONS;

// Map conversion status to outline tag severity
const getOutlineSeverity = (status) => {
    switch (status) {
        case 'pending_approval': return 'warning';
        case 'approved': return 'success';
        case 'rejected': return 'danger';
        case 'clarification_requested': return 'info';
        default: return 'secondary';
    }
};

// Border color based on status
const getBorderColor = (status) => {
    switch (status) {
        case 'pending_approval': return 'border-orange-500';
        case 'approved': return 'border-green-500';
        case 'rejected': return 'border-red-500';
        case 'clarification_requested': return 'border-blue-500';
        default: return 'border-primary';
    }
};

const ConversionsPage = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Loading state
    const [loading, setLoading] = useState(true);

    // Data state
    const [conversions, setConversions] = useState([]);

    // Filter state (shared between tabs)
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [loanTypeFilter, setLoanTypeFilter] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const rowsPerPage = 12;

    // Processing state
    const [processingId, setProcessingId] = useState(null);

    // Dialog states
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [showClarificationDialog, setShowClarificationDialog] = useState(false);
    const [selectedConversion, setSelectedConversion] = useState(null);

    // Dialog data
    const [rejectData, setRejectData] = useState({ reason: null, notes: '' });
    const [clarificationNotes, setClarificationNotes] = useState('');

    // View mode state
    const [viewMode, setViewMode] = useState('grid');
    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // Debounce search query
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Build query params from filters and pagination
    const buildQueryParams = () => {
        const params = { page, per_page: rowsPerPage };
        if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();
        if (loanTypeFilter) params.loan_type = loanTypeFilter;
        if (startDate) params.date_from = startDate.toISOString().split('T')[0];
        if (endDate) params.date_to = endDate.toISOString().split('T')[0];
        return params;
    };

    // Fetch conversions based on active tab
    const fetchConversions = async () => {
        setLoading(true);
        try {
            const params = buildQueryParams();
            const response = activeTab === 0
                ? await conversionService.getPendingApprovals(params)
                : await conversionService.getApprovedConversions(params);

            if (response.success) {
                const data = response.data || [];
                setConversions(Array.isArray(data) ? data : []);
                if (response.pagination) {
                    setTotalRecords(response.pagination.total || 0);
                }
            }
        } catch (error) {
            console.error('Failed to fetch conversions:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: `Failed to load ${activeTab === 0 ? 'pending' : 'approved'} conversions`
            });
        } finally {
            setLoading(false);
        }
    };

    // Reset pagination when filters change
    useEffect(() => {
        setFirst(0);
        setPage(1);
    }, [loanTypeFilter, startDate, endDate]);

    // Auto-fetch when tab, page, or filters change
    useEffect(() => {
        fetchConversions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, page, debouncedSearchQuery, loanTypeFilter, startDate, endDate]);

    // Handle tab change with pagination reset
    const handleTabChange = (e) => {
        setFirst(0);
        setPage(1);
        setActiveTab(e.index);
    };

    // Handle page change
    const onPageChange = (e) => {
        setFirst(e.first);
        setPage(Math.floor(e.first / rowsPerPage) + 1);
    };

    // Handle approve
    const handleApprove = (conversion) => {
        confirmDialog({
            message: (
                <div>
                    <p className="mb-2">Are you sure you want to approve this conversion?</p>
                    <div className="surface-ground border-round p-3">
                        <div className="flex justify-content-between mb-2">
                            <span className="text-600">Loan Disbursed:</span>
                            <span className="font-semibold">{formatCurrency(conversion.loan_amount_disbursed)}</span>
                        </div>
                        <div className="flex justify-content-between mb-2">
                            <span className="text-600">Commission from Lender:</span>
                            <span className="font-semibold">{formatCurrency(conversion.commission_received)}</span>
                        </div>
                        <Divider className="my-2" />
                        <div className="flex justify-content-between">
                            <span className="text-600">Lead Commission to You:</span>
                            <span className="font-bold text-primary">{formatCurrency(conversion.referral_fee_amount)}</span>
                        </div>
                        <div className="text-500 text-xs text-right">
                            {parseFloat((conversion.referral_fee_percentage || 0).toFixed(3))}% of disbursed amount
                        </div>
                    </div>
                    <p className="text-600 text-sm mt-3">
                        The lead commission will be transferred from the collaborator's wallet to your wallet.
                    </p>
                </div>
            ),
            header: 'Approve Conversion',
            icon: 'pi pi-check-circle',
            acceptClassName: 'p-button-success',
            acceptLabel: 'Approve',
            rejectLabel: 'Cancel',
            accept: async () => {
                setProcessingId(conversion.id);
                try {
                    const result = await conversionService.approveConversion(conversion.id);
                    if (result.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Approved',
                            detail: result.message || 'Conversion approved and payment processed',
                            life: 4000
                        });
                        fetchConversions();
                    } else {
                        throw new Error(result.message);
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to approve conversion',
                        life: 4000
                    });
                } finally {
                    setProcessingId(null);
                }
            }
        });
    };

    // Handle reject
    const handleRejectClick = (conversion) => {
        setSelectedConversion(conversion);
        setRejectData({ reason: null, notes: '' });
        setShowRejectDialog(true);
    };

    const confirmReject = async () => {
        if (!selectedConversion || !rejectData.reason) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please select a rejection reason'
            });
            return;
        }

        setProcessingId(selectedConversion.id);
        try {
            const result = await conversionService.rejectConversion(
                selectedConversion.id,
                rejectData.reason,
                rejectData.notes
            );
            if (result.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Rejected',
                    detail: 'Conversion has been rejected'
                });
                setShowRejectDialog(false);
                fetchConversions();
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to reject conversion'
            });
        } finally {
            setProcessingId(null);
        }
    };

    // Handle request clarification
    const handleClarificationClick = (conversion) => {
        setSelectedConversion(conversion);
        setClarificationNotes('');
        setShowClarificationDialog(true);
    };

    const confirmClarification = async () => {
        if (!selectedConversion || !clarificationNotes.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Required',
                detail: 'Please specify what clarification is needed'
            });
            return;
        }

        setProcessingId(selectedConversion.id);
        try {
            const result = await conversionService.requestClarification(
                selectedConversion.id,
                clarificationNotes
            );
            if (result.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Request Sent',
                    detail: 'Clarification request sent to collaborator'
                });
                setShowClarificationDialog(false);
                fetchConversions();
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to request clarification'
            });
        } finally {
            setProcessingId(null);
        }
    };

    // Navigate to collaboration details
    const viewDetails = (conversion) => {
        navigate(`/app/collaborations/${conversion.collaboration_id}`);
    };

    // Helper to get correct owner percentage from collaboration data
    const getOwnerPercentage = (conversion) => {
        const collaboration = conversion.collaboration;
        const distributionPercentage = collaboration?.commission_details?.distributions?.find(d => d.role === 'owner')?.percentage;
        if (distributionPercentage !== undefined && distributionPercentage !== null && distributionPercentage !== 100) {
            return distributionPercentage;
        }
        return conversion.referral_fee_percentage || 0;
    };

    // Status border color style
    const getStatusBorderColor = (status) => {
        switch (status) {
            case 'pending_approval': return 'var(--orange-500)';
            case 'approved': return 'var(--green-500)';
            case 'rejected': return 'var(--red-500)';
            case 'clarification_requested': return 'var(--blue-500)';
            default: return 'var(--primary-500)';
        }
    };

    // Loading skeleton - supports both grid and list views
    const renderSkeleton = () => {
        if (viewMode === 'grid') {
            return (
                <div className="grid">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="col-12 md:col-6 lg:col-4 p-2">
                            <div className="surface-card border-round border-1 surface-border p-2" style={{ borderLeftWidth: '3px' }}>
                                <div className="flex align-items-center gap-2 mb-2">
                                    <Skeleton shape="circle" size="2rem" />
                                    <div className="flex-1">
                                        <Skeleton width="70%" height="0.85rem" className="mb-1" />
                                        <Skeleton width="50%" height="0.6rem" />
                                    </div>
                                    <Skeleton width="50px" height="1rem" />
                                </div>
                                <Skeleton width="60%" height="0.8rem" className="mb-2" />
                                <Skeleton width="100%" height="3.5rem" className="mb-2" />
                                <div className="flex justify-content-between">
                                    <Skeleton width="60px" height="1.5rem" />
                                    <Skeleton width="80px" height="1.5rem" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="flex flex-column gap-2">
                {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="surface-card border-round border-1 surface-border px-3 py-2" style={{ borderLeftWidth: '3px' }}>
                        <div className="flex align-items-center gap-3">
                            <Skeleton shape="circle" size="2rem" />
                            <div className="flex-1">
                                <Skeleton width="140px" height="0.85rem" className="mb-1" />
                                <Skeleton width="90px" height="0.7rem" />
                            </div>
                            <Skeleton width="80px" height="0.85rem" />
                            <Skeleton width="70px" height="0.85rem" />
                            <Skeleton width="70px" height="0.85rem" />
                            <Skeleton width="60px" height="1rem" />
                            <div className="flex gap-1">
                                <Skeleton width="26px" height="26px" />
                                <Skeleton width="26px" height="26px" />
                                <Skeleton width="60px" height="26px" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // List Header Component
    const ListHeader = () => (
        <div className="hidden md:flex surface-100 border-round px-3 py-2 mb-2 align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
            <div style={{ width: '160px' }}>
                <span className="text-600 font-semibold">Submitter</span>
            </div>
            <div className="flex-1" style={{ minWidth: '140px' }}>
                <span className="text-600 font-semibold">Lead / Product</span>
            </div>
            <div className="hidden lg:block" style={{ width: '90px' }}>
                <span className="text-600 font-semibold">Disbursed</span>
            </div>
            <div style={{ width: '80px' }}>
                <span className="text-600 font-semibold">Commission</span>
            </div>
            <div style={{ width: '80px' }}>
                <span className="text-600 font-semibold">Your Fee</span>
            </div>
            <div style={{ width: '70px' }}>
                <span className="text-600 font-semibold">Status</span>
            </div>
            <div style={{ width: '120px' }}>
                <span className="text-600 font-semibold">Actions</span>
            </div>
        </div>
    );

    // List Row Component
    const ConversionRow = ({ conversion }) => {
        const statusConfig = getStatusConfig(conversion.status);
        const lead = conversion.collaboration?.lead || {};
        const productType = lead.product_type || lead.product_type_detailed;
        const ownerPercentage = getOwnerPercentage(conversion);
        const productConfig = getLoanProductConfig(productType);
        const isProcessing = processingId === conversion.id;
        const isPending = activeTab === 0;

        return (
            <div
                className="surface-card border-round border-1 surface-border px-3 py-2 mb-2 cursor-pointer hover:shadow-2 transition-all transition-duration-150"
                style={{ borderLeftWidth: '3px', borderLeftColor: getStatusBorderColor(conversion.status) }}
                onClick={() => viewDetails(conversion)}
            >
                <div className="flex align-items-center gap-2">
                    {/* Submitter */}
                    <div className="flex align-items-center gap-2" style={{ width: '160px' }}>
                        <Avatar
                            image={conversion.submitter?.avatar}
                            label={conversion.submitter?.name?.charAt(0)}
                            shape="circle"
                            className="bg-primary text-white flex-shrink-0"
                            style={{ width: '2rem', height: '2rem', fontSize: '0.7rem' }}
                        />
                        <div className="min-w-0">
                            <div className="font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>
                                {conversion.submitter?.name || 'Unknown'}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.65rem' }}>
                                {isPending ? formatTimeAgo(conversion.submitted_at) : formatTimeAgo(conversion.reviewed_at)}
                            </div>
                        </div>
                    </div>

                    {/* Lead / Product */}
                    <div className="flex align-items-center gap-2 flex-1" style={{ minWidth: '140px' }}>
                        <div
                            className="flex align-items-center justify-content-center border-round flex-shrink-0"
                            style={{ width: '28px', height: '28px', backgroundColor: '#eff6ff' }}
                        >
                            <i className={`pi ${productConfig?.icon || 'pi-briefcase'} text-primary`} style={{ fontSize: '0.75rem' }} />
                        </div>
                        <div className="min-w-0">
                            <div className="font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>
                                {lead.borrower_name || lead.masked_name || 'Unknown'}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.65rem' }}>
                                {productConfig?.label || productType || 'Loan'}
                            </div>
                        </div>
                    </div>

                    {/* Disbursed - hidden on smaller screens */}
                    <div className="hidden lg:block" style={{ width: '90px' }}>
                        <span className="text-green-600 font-bold" style={{ fontSize: '0.8rem' }}>
                            {formatCurrency(conversion.loan_amount_disbursed)}
                        </span>
                    </div>

                    {/* Commission */}
                    <div style={{ width: '80px' }}>
                        <span className="text-700 font-semibold" style={{ fontSize: '0.8rem' }}>
                            {formatCurrency(conversion.commission_received)}
                        </span>
                    </div>

                    {/* Your Fee */}
                    <div style={{ width: '80px' }}>
                        <span className="text-primary font-bold" style={{ fontSize: '0.8rem' }}>
                            {formatCurrency(conversion.referral_fee_amount)}
                        </span>
                        <div className="text-500" style={{ fontSize: '0.55rem' }}>
                            ({formatPercentage(ownerPercentage)})
                        </div>
                    </div>

                    {/* Status */}
                    <div style={{ width: '70px' }}>
                        <Tag
                            value={statusConfig.label}
                            style={{ ...outlineTagStyle(getOutlineSeverity(conversion.status)), fontSize: '0.6rem', padding: '2px 6px' }}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex align-items-center gap-1" style={{ width: '120px' }} onClick={(e) => e.stopPropagation()}>
                        <Button
                            icon="pi pi-eye"
                            className="p-button-text p-button-sm"
                            style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                            tooltip="View Details"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => viewDetails(conversion)}
                        />
                        {isPending && (
                            <>
                                <Button
                                    icon="pi pi-question-circle"
                                    className="p-button-outlined p-button-sm"
                                    style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.65rem' }}
                                    tooltip="Clarification"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => handleClarificationClick(conversion)}
                                    disabled={isProcessing}
                                />
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-danger p-button-outlined p-button-sm"
                                    style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.65rem' }}
                                    tooltip="Reject"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => handleRejectClick(conversion)}
                                    disabled={isProcessing}
                                />
                                <Button
                                    icon="pi pi-check"
                                    className="p-button-success p-button-sm"
                                    style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.65rem' }}
                                    tooltip="Approve"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => handleApprove(conversion)}
                                    loading={isProcessing}
                                />
                            </>
                        )}
                    </div>
                </div>

                {/* Clarification/Rejection info - shown below row */}
                {(conversion.clarification || conversion.rejection) && (
                    <div className="mt-2 pt-2 border-top-1 surface-border">
                        {conversion.clarification && (
                            <div className="flex align-items-start gap-2" style={{ fontSize: '0.65rem' }}>
                                <i className="pi pi-question-circle text-orange-500 mt-1" style={{ fontSize: '0.6rem' }} />
                                <div className="flex-1">
                                    <span className="font-medium text-orange-700">Clarification: </span>
                                    <span className="text-700">{conversion.clarification.notes}</span>
                                    {conversion.clarification.response && (
                                        <div className="mt-1 text-green-700">
                                            <i className="pi pi-reply mr-1" style={{ fontSize: '0.55rem' }} />
                                            Response: {conversion.clarification.response}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {conversion.rejection && (
                            <div className="flex align-items-start gap-2" style={{ fontSize: '0.65rem' }}>
                                <i className="pi pi-times-circle text-red-500 mt-1" style={{ fontSize: '0.6rem' }} />
                                <div>
                                    <span className="font-medium text-red-700">Rejected: </span>
                                    <span className="text-700">{conversion.rejection.reason}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Card Component for Grid View
    const ConversionCard = ({ conversion }) => {
        const statusConfig = getStatusConfig(conversion.status);
        const lead = conversion.collaboration?.lead || {};
        const productType = lead.product_type || lead.product_type_detailed;
        const ownerPercentage = getOwnerPercentage(conversion);
        const productConfig = getLoanProductConfig(productType);
        const isProcessing = processingId === conversion.id;
        const isPending = activeTab === 0;

        // Status background colors
        const getStatusBg = (status) => {
            switch (status) {
                case 'pending_approval': return 'bg-orange-50';
                case 'approved': return 'bg-green-50';
                case 'rejected': return 'bg-red-50';
                case 'clarification_requested': return 'bg-blue-50';
                default: return 'surface-100';
            }
        };

        return (
            <div className={`surface-card border-round shadow-1 overflow-hidden h-full flex flex-column`} style={{ borderLeft: `3px solid ${getStatusBorderColor(conversion.status)}` }}>
                {/* Card Header - Status Banner */}
                <div className={`p-2 ${getStatusBg(conversion.status)} flex align-items-center justify-content-between`}>
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            image={conversion.submitter?.avatar}
                            label={conversion.submitter?.name?.charAt(0)}
                            shape="circle"
                            className="bg-primary text-white flex-shrink-0"
                            style={{ width: '2rem', height: '2rem', fontSize: '0.75rem' }}
                        />
                        <div>
                            <span className="font-semibold text-900 block" style={{ fontSize: '0.8rem' }}>
                                {conversion.submitter?.name || 'Unknown'}
                            </span>
                            <span className="text-500" style={{ fontSize: '0.6rem' }}>
                                {isPending
                                    ? `Submitted ${formatTimeAgo(conversion.submitted_at)}`
                                    : `Approved ${formatTimeAgo(conversion.reviewed_at)}`
                                }
                            </span>
                        </div>
                    </div>
                    <Tag
                        value={statusConfig.label}
                        style={{ ...outlineTagStyle(getOutlineSeverity(conversion.status)), fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}
                    />
                </div>

                {/* Card Body */}
                <div className="p-2 flex-1 flex flex-column">
                    {/* Lead Info */}
                    {lead && (
                        <div className="mb-2">
                            <div className="flex align-items-center gap-1 mb-1">
                                <i className={`pi ${productConfig?.icon || 'pi-briefcase'} text-primary`} style={{ fontSize: '0.75rem' }} />
                                <span className="font-medium text-900" style={{ fontSize: '0.75rem' }}>
                                    {productType ? (productConfig?.label || productType) : 'Loan'}
                                </span>
                            </div>
                            <div className="text-600" style={{ fontSize: '0.7rem' }}>
                                {lead.borrower_name || lead.masked_name}
                            </div>
                        </div>
                    )}

                    {/* Lender Info */}
                    {(conversion.lender_name || conversion.lender_branch) && (
                        <div className="surface-100 border-round p-2 mb-2" style={{ fontSize: '0.65rem' }}>
                            {conversion.lender_name && (
                                <div className="flex align-items-center gap-1 mb-1">
                                    <i className="pi pi-building text-500" style={{ fontSize: '0.6rem' }} />
                                    <span className="text-700">{conversion.lender_name}</span>
                                </div>
                            )}
                            {conversion.lender_branch && (
                                <div className="flex align-items-center gap-1">
                                    <i className="pi pi-map-marker text-500" style={{ fontSize: '0.6rem' }} />
                                    <span className="text-600">{conversion.lender_branch}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Financial Figures - Grid Layout */}
                    <div className="grid m-0 mb-2 surface-50 border-round p-2">
                        <div className="col-4 p-1 text-center">
                            <div className="text-500" style={{ fontSize: '0.55rem' }}>Disbursed</div>
                            <div className="font-bold text-900" style={{ fontSize: '0.8rem' }}>
                                {formatCurrency(conversion.loan_amount_disbursed)}
                            </div>
                        </div>
                        <div className="col-4 p-1 text-center border-left-1 border-right-1 surface-border">
                            <div className="text-500" style={{ fontSize: '0.55rem' }}>Commission</div>
                            <div className="font-semibold text-700" style={{ fontSize: '0.75rem' }}>
                                {formatCurrency(conversion.commission_received)}
                            </div>
                        </div>
                        <div className="col-4 p-1 text-center">
                            <div className="text-500" style={{ fontSize: '0.55rem' }}>
                                {isPending ? 'Your Fee' : 'Fee Received'}
                            </div>
                            <div className="font-bold text-primary" style={{ fontSize: '0.8rem' }}>
                                {formatCurrency(conversion.referral_fee_amount)}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.5rem' }}>
                                ({formatPercentage(ownerPercentage)})
                            </div>
                        </div>
                    </div>

                    {/* Documents & Date Row */}
                    <div className="flex align-items-center justify-content-between mb-2" style={{ fontSize: '0.6rem' }}>
                        <div className="flex align-items-center gap-1">
                            <i className="pi pi-paperclip text-500" style={{ fontSize: '0.6rem' }} />
                            <span className="text-600">{conversion.documents_count || 0} docs</span>
                            {conversion.has_required_documents ? (
                                <Tag value="Complete" style={{ ...outlineTagStyle('success'), fontSize: '0.5rem', padding: '0 3px' }} />
                            ) : (
                                <Tag value="Missing" style={{ ...outlineTagStyle('warning'), fontSize: '0.5rem', padding: '0 3px' }} />
                            )}
                        </div>
                        {conversion.disbursement_date && (
                            <span className="text-400">
                                <i className="pi pi-calendar mr-1" style={{ fontSize: '0.5rem' }} />
                                {formatDate(conversion.disbursement_date)}
                            </span>
                        )}
                    </div>

                    {/* Clarification history */}
                    {conversion.clarification && (
                        <div className="surface-100 border-round p-2 mb-2">
                            <div className="flex align-items-center gap-1 mb-1">
                                <i className="pi pi-question-circle text-orange-500" style={{ fontSize: '0.65rem' }} />
                                <span className="font-medium text-orange-700" style={{ fontSize: '0.65rem' }}>Clarification Requested</span>
                            </div>
                            <div className="text-700" style={{ fontSize: '0.65rem' }}>{conversion.clarification.notes}</div>
                            {conversion.clarification.response && (
                                <div className="mt-1">
                                    <span className="text-green-700 font-medium" style={{ fontSize: '0.6rem' }}>
                                        <i className="pi pi-reply mr-1" style={{ fontSize: '0.55rem' }} />
                                        Response:
                                    </span>
                                    <span className="text-700" style={{ fontSize: '0.6rem' }}> {conversion.clarification.response}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Rejection info */}
                    {conversion.rejection && (
                        <div className="surface-100 border-round p-2 mb-2">
                            <div className="flex align-items-center gap-1 mb-1">
                                <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.65rem' }} />
                                <span className="font-medium text-red-700" style={{ fontSize: '0.65rem' }}>
                                    {conversion.status === 'rejected' ? 'Rejected' : 'Previously Rejected'}
                                </span>
                            </div>
                            <div className="text-700" style={{ fontSize: '0.65rem' }}>
                                <strong>Reason:</strong> {conversion.rejection.reason}
                            </div>
                        </div>
                    )}

                    {/* Spacer to push actions to bottom */}
                    <div className="flex-1"></div>

                    {/* Action Buttons */}
                    <div className="flex align-items-center justify-content-between pt-2 border-top-1 surface-border mt-auto">
                        <Button
                            icon="pi pi-eye"
                            label="View"
                            className="p-button-text p-button-sm"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => viewDetails(conversion)}
                        />
                        {isPending && (
                            <div className="flex align-items-center gap-1">
                                <Button
                                    icon="pi pi-question-circle"
                                    className="p-button-outlined p-button-sm"
                                    style={{ width: '1.8rem', height: '1.8rem', fontSize: '0.65rem' }}
                                    tooltip="Clarification"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => handleClarificationClick(conversion)}
                                    disabled={isProcessing}
                                />
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-danger p-button-outlined p-button-sm"
                                    style={{ width: '1.8rem', height: '1.8rem', fontSize: '0.65rem' }}
                                    tooltip="Reject"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => handleRejectClick(conversion)}
                                    disabled={isProcessing}
                                />
                                <Button
                                    icon="pi pi-check"
                                    label="Approve"
                                    className="p-button-success p-button-sm"
                                    style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                                    onClick={() => handleApprove(conversion)}
                                    loading={isProcessing}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Mobile Card View - Always used on mobile (matches desktop ConversionCard)
    const renderMobileCard = (conversion) => {
        const statusConfig = getStatusConfig(conversion.status);
        const lead = conversion.collaboration?.lead || {};
        const productType = lead.product_type || lead.product_type_detailed;
        const ownerPercentage = getOwnerPercentage(conversion);
        const productConfig = getLoanProductConfig(productType);
        const isProcessing = processingId === conversion.id;
        const isPending = activeTab === 0;

        // Status background colors
        const getStatusBg = (status) => {
            switch (status) {
                case 'pending_approval': return 'bg-orange-50';
                case 'approved': return 'bg-green-50';
                case 'rejected': return 'bg-red-50';
                case 'clarification_requested': return 'bg-blue-50';
                default: return 'surface-100';
            }
        };

        return (
            <div
                key={conversion.id}
                className="surface-card border-round shadow-1 mb-2 overflow-hidden"
                style={{ borderLeft: `3px solid ${getStatusBorderColor(conversion.status)}` }}
            >
                {/* Header with Status Background */}
                <div className={`p-2 ${getStatusBg(conversion.status)} flex align-items-center justify-content-between`}>
                    <div className="flex align-items-center gap-2 overflow-hidden" style={{ maxWidth: '70%' }}>
                        <Avatar
                            image={conversion.submitter?.avatar}
                            label={conversion.submitter?.name?.charAt(0)}
                            shape="circle"
                            className="bg-primary text-white flex-shrink-0"
                            style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.65rem' }}
                        />
                        <div className="overflow-hidden">
                            <div className="font-semibold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>
                                {conversion.submitter?.name || 'Unknown'}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>
                                {isPending ? `Submitted ${formatTimeAgo(conversion.submitted_at)}` : `Approved ${formatTimeAgo(conversion.reviewed_at)}`}
                            </div>
                        </div>
                    </div>
                    <Tag
                        value={statusConfig.label}
                        style={{ ...outlineTagStyle(getOutlineSeverity(conversion.status)), fontSize: '0.6rem', padding: '2px 6px' }}
                        className="flex-shrink-0"
                    />
                </div>

                {/* Card Body */}
                <div className="p-2">
                    {/* Lead Info */}
                    <div className="flex align-items-center gap-2 mb-2">
                        <div
                            className="flex align-items-center justify-content-center border-round flex-shrink-0"
                            style={{ width: '24px', height: '24px', backgroundColor: '#eff6ff' }}
                        >
                            <i className={`pi ${productConfig?.icon || 'pi-briefcase'} text-primary`} style={{ fontSize: '0.7rem' }} />
                        </div>
                        <div className="overflow-hidden">
                            <div className="font-medium text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.75rem' }}>
                                {lead.borrower_name || lead.masked_name || 'Unknown'}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>
                                {productConfig?.label || productType || 'Loan'}
                            </div>
                        </div>
                    </div>

                    {/* Lender Info */}
                    {(conversion.lender_name || conversion.lender_branch) && (
                        <div className="surface-100 border-round p-2 mb-2" style={{ fontSize: '0.65rem' }}>
                            {conversion.lender_name && (
                                <div className="flex align-items-center gap-1 mb-1">
                                    <i className="pi pi-building text-500" style={{ fontSize: '0.6rem' }} />
                                    <span className="text-700">{conversion.lender_name}</span>
                                </div>
                            )}
                            {conversion.lender_branch && (
                                <div className="flex align-items-center gap-1">
                                    <i className="pi pi-map-marker text-500" style={{ fontSize: '0.6rem' }} />
                                    <span className="text-600">{conversion.lender_branch}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Financial Grid - 2x2 */}
                    <div className="grid mb-2">
                        <div className="col-6 p-1">
                            <div className="surface-50 border-round p-1 text-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>Disbursed</div>
                                <div className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatCurrency(conversion.loan_amount_disbursed)}</div>
                            </div>
                        </div>
                        <div className="col-6 p-1">
                            <div className="surface-50 border-round p-1 text-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>Commission</div>
                                <div className="text-700 font-semibold" style={{ fontSize: '0.7rem' }}>{formatCurrency(conversion.commission_received)}</div>
                            </div>
                        </div>
                        <div className="col-6 p-1">
                            <div className="surface-50 border-round p-1 text-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>{isPending ? 'Your Fee' : 'Received'}</div>
                                <div className="text-primary font-bold" style={{ fontSize: '0.75rem' }}>{formatCurrency(conversion.referral_fee_amount)}</div>
                            </div>
                        </div>
                        <div className="col-6 p-1">
                            <div className="surface-50 border-round p-1 text-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>Fee %</div>
                                <div className="text-700 font-medium" style={{ fontSize: '0.7rem' }}>{formatPercentage(ownerPercentage)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Documents & Date Row */}
                    <div className="flex align-items-center justify-content-between mb-2" style={{ fontSize: '0.6rem' }}>
                        <div className="flex align-items-center gap-1">
                            <i className="pi pi-paperclip text-500" style={{ fontSize: '0.6rem' }} />
                            <span className="text-600">{conversion.documents_count || 0} docs</span>
                            {conversion.has_required_documents ? (
                                <Tag value="Complete" style={{ ...outlineTagStyle('success'), fontSize: '0.5rem', padding: '0 3px' }} />
                            ) : (
                                <Tag value="Missing" style={{ ...outlineTagStyle('warning'), fontSize: '0.5rem', padding: '0 3px' }} />
                            )}
                        </div>
                        {conversion.disbursement_date && (
                            <span className="text-400">
                                <i className="pi pi-calendar mr-1" style={{ fontSize: '0.5rem' }} />
                                {formatDate(conversion.disbursement_date)}
                            </span>
                        )}
                    </div>

                    {/* Clarification history */}
                    {conversion.clarification && (
                        <div className="surface-100 border-round p-2 mb-2">
                            <div className="flex align-items-center gap-1 mb-1">
                                <i className="pi pi-question-circle text-orange-500" style={{ fontSize: '0.65rem' }} />
                                <span className="font-medium text-orange-700" style={{ fontSize: '0.65rem' }}>Clarification Requested</span>
                            </div>
                            <div className="text-700" style={{ fontSize: '0.65rem' }}>{conversion.clarification.notes}</div>
                            {conversion.clarification.response && (
                                <div className="mt-1">
                                    <span className="text-green-700 font-medium" style={{ fontSize: '0.6rem' }}>
                                        <i className="pi pi-reply mr-1" style={{ fontSize: '0.55rem' }} />
                                        Response:
                                    </span>
                                    <span className="text-700" style={{ fontSize: '0.6rem' }}> {conversion.clarification.response}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Rejection info */}
                    {conversion.rejection && (
                        <div className="surface-100 border-round p-2 mb-2">
                            <div className="flex align-items-center gap-1 mb-1">
                                <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.65rem' }} />
                                <span className="font-medium text-red-700" style={{ fontSize: '0.65rem' }}>
                                    {conversion.status === 'rejected' ? 'Rejected' : 'Previously Rejected'}
                                </span>
                            </div>
                            <div className="text-700" style={{ fontSize: '0.65rem' }}>
                                <strong>Reason:</strong> {conversion.rejection.reason}
                            </div>
                        </div>
                    )}

                    {/* Actions Row */}
                    <div className="flex align-items-center justify-content-between pt-2 border-top-1 surface-border">
                        <Button
                            icon="pi pi-eye"
                            label="View"
                            className="p-button-text p-button-sm"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => viewDetails(conversion)}
                        />
                        {isPending && (
                            <div className="flex align-items-center gap-1">
                                <Button
                                    icon="pi pi-question-circle"
                                    className="p-button-outlined p-button-sm"
                                    style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.65rem' }}
                                    onClick={() => handleClarificationClick(conversion)}
                                    disabled={isProcessing}
                                />
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-danger p-button-outlined p-button-sm"
                                    style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.65rem' }}
                                    onClick={() => handleRejectClick(conversion)}
                                    disabled={isProcessing}
                                />
                                <Button
                                    icon="pi pi-check"
                                    className="p-button-success p-button-sm"
                                    style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.65rem' }}
                                    onClick={() => handleApprove(conversion)}
                                    loading={isProcessing}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Mobile List rendering with pagination
    const renderMobileList = (data) => {
        if (data.length === 0) {
            return emptyTemplate();
        }

        return (
            <div className="p-2">
                {data.map((conversion) => renderMobileCard(conversion))}
                {totalRecords > rowsPerPage && (
                    <Paginator
                        first={first}
                        rows={rowsPerPage}
                        totalRecords={totalRecords || 0}
                        onPageChange={onPageChange}
                        className="mt-2 p-paginator-sm"
                        template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{currentPage}/{totalPages}"
                    />
                )}
            </div>
        );
    };

    // Compact empty state
    const emptyTemplate = () => {
        const isPending = activeTab === 0;
        return (
            <div className="flex flex-column align-items-center justify-content-center py-4">
                <div
                    className="border-circle p-3 mb-2 flex align-items-center justify-content-center"
                    style={{
                        backgroundColor: isPending ? 'var(--orange-50)' : 'var(--green-50)',
                        width: '3rem', height: '3rem'
                    }}
                >
                    <i
                        className={`pi ${isPending ? 'pi-inbox' : 'pi-check-circle'}`}
                        style={{
                            color: isPending ? 'var(--orange-500)' : 'var(--green-500)',
                            fontSize: '1.25rem'
                        }}
                    />
                </div>
                <h3 className="text-900 font-semibold mb-1 m-0" style={{ fontSize: '0.85rem' }}>
                    {isPending ? 'No Pending Conversions' : 'No Converted Leads Yet'}
                </h3>
                <p className="text-500 mb-2 text-center px-3 line-height-3 m-0" style={{ maxWidth: '260px', fontSize: '0.7rem' }}>
                    {isPending
                        ? "No conversion submissions waiting for your approval."
                        : "No conversions approved yet."
                    }
                </p>
                <Button
                    label="View Lead Deals"
                    icon="pi pi-arrow-right"
                    className="p-button-text mt-1"
                    style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem' }}
                    onClick={() => navigate('/app/collaborations')}
                />
            </div>
        );
    };

    // Render desktop content (grid or list based on viewMode)
    const renderDesktopContent = () => {
        if (loading) {
            return renderSkeleton();
        }

        if (conversions.length === 0) {
            return emptyTemplate();
        }

        if (viewMode === 'grid') {
            return (
                <>
                    <div className="grid">
                        {conversions.map((conversion) => (
                            <div key={conversion.id} className="col-12 md:col-6 lg:col-4 p-2">
                                <ConversionCard conversion={conversion} />
                            </div>
                        ))}
                    </div>
                    {/* Paginator for Grid View */}
                    <div style={{ visibility: totalRecords > rowsPerPage ? 'visible' : 'hidden' }}>
                        <Paginator
                            first={first}
                            rows={rowsPerPage}
                            totalRecords={totalRecords || 0}
                            onPageChange={onPageChange}
                            className="mt-2 p-paginator-sm"
                            template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                            currentPageReportTemplate="{currentPage}/{totalPages}"
                        />
                    </div>
                </>
            );
        }

        // List view
        return (
            <>
                <ListHeader />
                {conversions.map((conversion) => (
                    <ConversionRow key={conversion.id} conversion={conversion} />
                ))}
                {/* Paginator for List View */}
                <div style={{ visibility: totalRecords > rowsPerPage ? 'visible' : 'hidden' }}>
                    <Paginator
                        first={first}
                        rows={rowsPerPage}
                        totalRecords={totalRecords || 0}
                        onPageChange={onPageChange}
                        className="mt-2 p-paginator-sm"
                        template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{currentPage}/{totalPages}"
                    />
                </div>
            </>
        );
    };

    return (
        <div className="conversions-page">
            <NavigationTabMenu tabs={LEAD_DEALS_TABS} />
            <Toast ref={toast} />
            <ConfirmDialog />
            <Tooltip target=".custom-tooltip" />

            {/* Page Header - Compact */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="flex align-items-center justify-content-between">
                    <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>Lead Conversions</h2>
                    <div className="flex align-items-center gap-1">
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-sm"
                            style={{ fontSize: '0.7rem', width: '1.75rem', height: '1.75rem' }}
                            onClick={() => fetchConversions()}
                            loading={loading}
                            tooltip="Refresh"
                        />
                    </div>
                </div>
            </div>

            {/* Filters Row - Grid layout for proper alignment */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="grid align-items-end m-0">
                    {/* Search */}
                    <div className="col-6 md:col-3 lg:col-3 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Search</label>
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                            <InputText
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Collaborator, lead..."
                                className="w-full p-inputtext-sm"
                                style={{ fontSize: '0.7rem', padding: '0.5rem 0.5rem 0.5rem 2rem' }}
                            />
                        </span>
                    </div>

                    {/* Loan Type */}
                    <div className="col-6 md:col-3 lg:col-3 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Loan Type</label>
                        <Dropdown
                            value={loanTypeFilter}
                            options={LOAN_TYPE_OPTIONS}
                            onChange={(e) => setLoanTypeFilter(e.value)}
                            placeholder="All Products"
                            optionLabel="label"
                            optionValue="value"
                            showClear
                            filter
                            filterPlaceholder="Search..."
                            className="w-full p-inputtext-sm"
                            panelStyle={{ fontSize: '0.7rem' }}
                            pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem', padding: '0.4rem 0.75rem' } }, filterInput: { style: { fontSize: '0.7rem' } } }}
                        />
                    </div>

                    {/* From Date */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>From Date</label>
                        <Calendar
                            value={startDate}
                            onChange={(e) => setStartDate(e.value)}
                            placeholder="From"
                            className="w-full p-inputtext-sm"
                            showIcon
                            inputStyle={{ fontSize: '0.7rem' }}
                            dateFormat="dd M yy"
                            showButtonBar
                            maxDate={endDate || undefined}
                            pt={{
                                input: { style: { fontSize: '0.7rem' } },
                                dropdownButton: { style: { width: '2rem', padding: '0' } },
                                icon: { style: { fontSize: '0.75rem' } }
                            }}
                        />
                    </div>

                    {/* To Date */}
                    <div className="col-6 md:col-2 lg:col-2 p-1">
                        <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>To Date</label>
                        <Calendar
                            value={endDate}
                            onChange={(e) => setEndDate(e.value)}
                            placeholder="To"
                            className="w-full p-inputtext-sm"
                            showIcon
                            inputStyle={{ fontSize: '0.7rem' }}
                            dateFormat="dd M yy"
                            showButtonBar
                            minDate={startDate || undefined}
                            pt={{
                                input: { style: { fontSize: '0.7rem' } },
                                dropdownButton: { style: { width: '2rem', padding: '0' } },
                                icon: { style: { fontSize: '0.75rem' } }
                            }}
                        />
                    </div>

                    {/* View Toggle + Refresh */}
                    <div className="col-6 md:col-2 lg:col-2 p-1 flex align-items-end justify-content-end gap-1">
                        <div className="hidden md:flex align-items-center gap-1">
                            <SelectButton
                                value={viewMode}
                                options={viewOptions}
                                onChange={(e) => e.value && setViewMode(e.value)}
                                optionLabel="icon"
                                itemTemplate={(option) => <i className={option.icon} style={{ fontSize: '0.7rem' }} />}
                                style={{ transform: 'scale(0.85)' }}
                            />
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-rounded"
                            onClick={fetchConversions}
                            loading={loading}
                            tooltip="Refresh"
                            tooltipOptions={{ position: 'top' }}
                            style={{ width: '1.75rem', height: '1.75rem' }}
                        />
                    </div>
                </div>

                {/* Active filters */}
                {(loanTypeFilter || startDate || endDate) && (
                    <div className="flex align-items-center justify-content-between mt-1 pt-1 border-top-1 surface-border">
                        <span className="text-500" style={{ fontSize: '0.7rem' }}>
                            {[loanTypeFilter, startDate, endDate].filter(Boolean).length} filter{[loanTypeFilter, startDate, endDate].filter(Boolean).length > 1 ? 's' : ''} active
                        </span>
                        <Button
                            label="Clear"
                            icon="pi pi-filter-slash"
                            className="p-button-text p-button-sm"
                            onClick={() => { setLoanTypeFilter(null); setStartDate(null); setEndDate(null); setSearchQuery(''); setDebouncedSearchQuery(''); }}
                            style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}
                        />
                    </div>
                )}
            </div>

            {/* Compact Tabs */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <TabView activeIndex={activeTab} onTabChange={handleTabChange} className="compact-tabview p-0">
                    <TabPanel header={
                        <span style={{ fontSize: '0.75rem' }}>
                            <i className="pi pi-clock mr-1" style={{ fontSize: '0.7rem' }} />
                            Pending
                            {totalRecords > 0 && activeTab === 0 && (
                                <Badge value={totalRecords} severity="warning" className="ml-1" style={{ fontSize: '0.55rem', padding: '0 3px', minWidth: '14px', height: '12px', lineHeight: '12px' }} />
                            )}
                        </span>
                    } />

                    <TabPanel header={
                        <span style={{ fontSize: '0.75rem' }}>
                            <i className="pi pi-check-circle mr-1" style={{ fontSize: '0.7rem' }} />
                            Converted
                            {totalRecords > 0 && activeTab === 1 && (
                                <Badge value={totalRecords} severity="success" className="ml-1" style={{ fontSize: '0.55rem', padding: '0 3px', minWidth: '14px', height: '12px', lineHeight: '12px' }} />
                            )}
                        </span>
                    } />
                </TabView>
            </div>

            {/* Content - Desktop: Grid/List View, Mobile: Card List */}
            <div className="surface-card border-round shadow-1">
                {/* Desktop: Grid or List View */}
                <div className="hidden md:block p-2">
                    {renderDesktopContent()}
                </div>

                {/* Mobile: Card List */}
                <div className="block md:hidden">
                    {loading ? renderSkeleton() : renderMobileList(conversions)}
                </div>
            </div>

            {/* Reject Dialog */}
            <Dialog
                header="Reject Conversion"
                visible={showRejectDialog}
                onHide={() => setShowRejectDialog(false)}
                style={{ width: '450px' }}
                footer={
                    <div>
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowRejectDialog(false)} />
                        <Button
                            label="Reject"
                            className="p-button-danger"
                            onClick={confirmReject}
                            loading={processingId === selectedConversion?.id}
                        />
                    </div>
                }
            >
                <p className="text-600 mb-4">
                    This will reject the conversion submission. The collaborator can submit a new conversion later.
                </p>
                <div className="field">
                    <label className="font-semibold block mb-2">Rejection Reason *</label>
                    <Dropdown
                        value={rejectData.reason}
                        options={REJECTION_REASONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => setRejectData({ ...rejectData, reason: e.value })}
                        placeholder="Select reason"
                        className="w-full"
                    />
                </div>
                <div className="field">
                    <label className="font-semibold block mb-2">Additional Notes</label>
                    <InputTextarea
                        value={rejectData.notes}
                        onChange={(e) => setRejectData({ ...rejectData, notes: e.target.value })}
                        rows={3}
                        placeholder="Optional notes explaining the rejection..."
                        className="w-full"
                    />
                </div>
            </Dialog>

            {/* Clarification Request Dialog */}
            <Dialog
                header="Request Clarification"
                visible={showClarificationDialog}
                onHide={() => setShowClarificationDialog(false)}
                style={{ width: '450px' }}
                footer={
                    <div>
                        <Button label="Cancel" className="p-button-text" onClick={() => setShowClarificationDialog(false)} />
                        <Button
                            label="Send Request"
                            onClick={confirmClarification}
                            loading={processingId === selectedConversion?.id}
                        />
                    </div>
                }
            >
                <p className="text-600 mb-4">
                    Request additional information or corrections from the collaborator before making a decision.
                </p>
                <div className="field">
                    <label className="font-semibold block mb-2">What clarification is needed? *</label>
                    <InputTextarea
                        value={clarificationNotes}
                        onChange={(e) => setClarificationNotes(e.target.value)}
                        rows={4}
                        placeholder="Describe what additional information or corrections you need..."
                        className="w-full"
                    />
                </div>
            </Dialog>

            {/* CSS overrides for compact Calendar/Dropdown */}
            <style>{`
                .conversions-page .p-calendar .p-inputtext {
                    padding: 0.5rem 0.5rem;
                    font-size: 0.7rem;
                }
                .conversions-page .p-calendar .p-datepicker-trigger {
                    width: 2rem;
                    padding: 0;
                }
                .conversions-page .p-calendar .p-datepicker-trigger .p-icon {
                    width: 0.75rem;
                    height: 0.75rem;
                }
                .conversions-page .p-dropdown .p-dropdown-label {
                    padding: 0.5rem 0.5rem;
                    font-size: 0.7rem;
                }
                .conversions-page .p-dropdown .p-dropdown-trigger {
                    width: 2rem;
                }
                .conversions-page .compact-tabview .p-tabview-panels {
                    padding: 0;
                    display: none;
                }
                @media screen and (max-width: 768px) {
                    .conversions-page .p-calendar,
                    .conversions-page .p-dropdown {
                        font-size: 0.65rem;
                    }
                }
            `}</style>
        </div>
    );
};

// Keep the export name for backwards compatibility with App.js
export default ConversionsPage;
